import { useEffect, useState } from "react";

/* Software-keyboard avoidance for the AI Assistant composer (P0 fix 5).

   iOS Safari never resizes the layout viewport when the keyboard opens —
   only window.visualViewport reports the shrinking visible area, so a
   bottom-anchored composer ends up underneath the keyboard. This hook
   measures the occluded strip:

     offset = max(0, innerHeight - visualViewport.height - visualViewport.offsetTop)

   which is the distance between the visual viewport's bottom edge and the
   layout viewport's bottom edge (≈ the keyboard height while it is open).
   The composer translates itself up by that amount.

   Android Chrome (with interactive-widget=resizes-content in index.html)
   resizes the layout viewport instead, so innerHeight ≈ visualViewport.height
   and the offset resolves to 0 → no transform. The hook therefore only ever
   does real work on iOS Safari, but is harmless everywhere else.

   Phone-width gate is evaluated once on mount: desktop windows never grow a
   software keyboard, so listeners there would only burn cycles. */

const useVisualViewportOffset = () => {
  const [offset, setOffset] = useState(0);

  useEffect(() => {
    const viewport = window.visualViewport;
    if (!viewport) return undefined;
    if (!window.matchMedia("(max-width: 767px)").matches) return undefined;

    const update = () => {
      const next = Math.max(
        0,
        window.innerHeight - viewport.height - viewport.offsetTop
      );
      setOffset((current) => (current === next ? current : next));
    };

    update();
    viewport.addEventListener("resize", update);
    viewport.addEventListener("scroll", update);
    return () => {
      viewport.removeEventListener("resize", update);
      viewport.removeEventListener("scroll", update);
    };
  }, []);

  return offset;
};

export default useVisualViewportOffset;
